import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import type { ReleaseNote } from '../../services/releaseNotes.service';
import { describeApiError } from '../errorMessage';

interface Props {
  open: boolean;
  note: ReleaseNote | null;
  onClose: () => void;
  onConfirm: (failedOnly: boolean) => Promise<void>;
}

/**
 * Confirms an email resend.
 *
 * This is deliberately a blocking confirm rather than a one-click action: we
 * don't keep per-recipient delivery records, so "resend to everyone" genuinely
 * re-emails people who already received the note.
 */
const ResendDialog: React.FC<Props> = ({ open, note, onClose, onConfirm }) => {
  const [failedOnly, setFailedOnly] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const failedCount = note?.failedCount ?? 0;

  const handleConfirm = async () => {
    setIsSending(true);
    setError(null);
    try {
      await onConfirm(failedOnly);
      onClose();
    } catch (err: any) {
      setError(describeApiError(err, 'Failed to resend'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={isSending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Resend release note email</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" sx={{ mb: 2 }}>
          Resending <strong>{note?.title}</strong>.
        </Typography>

        <RadioGroup
          value={failedOnly ? 'failed' : 'all'}
          onChange={(e) => setFailedOnly(e.target.value === 'failed')}
        >
          <FormControlLabel
            value="all"
            control={<Radio size="small" />}
            disabled={isSending}
            label="Send to the entire current audience"
          />
          <FormControlLabel
            value="failed"
            control={<Radio size="small" />}
            disabled={isSending || failedCount === 0}
            label={
              failedCount > 0
                ? `Retry the ${failedCount} address${failedCount === 1 ? '' : 'es'} that failed`
                : 'Retry failed addresses (none failed)'
            }
          />
        </RadioGroup>

        {!failedOnly && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Delivery is not tracked per recipient, so this emails everyone in the
            audience again — including people who already received it.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isSending} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleConfirm} disabled={isSending} variant="contained">
          {isSending ? 'Sending…' : 'Resend'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResendDialog;
